import * as React from "react";
import { Alert, Box, Button, Card, Chip, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { ArrowCounterClockwiseIcon, ProhibitIcon } from "@phosphor-icons/react";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { useNavigate } from "react-router-dom";

import {
	REASON_LABELS,
	SOURCE_LABELS,
	Suppression,
	suppressionsKeys,
} from "@/hooks/api/use-suppressions";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column } from "@/components/customTable/types";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { AddToDoNotCallDialog } from "./add-dialog";
import { ReleaseSuppressionDialog } from "./release-dialog";

function formatDate(value: string | null): string {
	if (!value) return "-";
	return new Date(value).toLocaleString();
}

export function DoNotCall(): React.JSX.Element {
	const [showReleased, setShowReleased] = React.useState(false);
	const [isAddOpen, setIsAddOpen] = React.useState(false);
	const [toRelease, setToRelease] = React.useState<Suppression | null>(null);
	const navigate = useNavigate();

	const {
		items: records,
		isLoading,
		totalItems,
		refetchData,
	} = useCachedTableStateWithUrl<Suppression>({
		queryKey: [...suppressionsKeys.lists(), showReleased ? "released" : "active"],
		endpoint: `/crm/suppressions?active=${showReleased ? "false" : "true"}`,
	});

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: { id: "phone", label: "Number", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "phone",
				getCellValue: (row: Suppression) =>
					row.phone ? (
						// Linked to the contact wherever we know which one it is, so the call that put
						// them here is one click away rather than a copy-paste into the contact search.
						// A number added by hand for a person we hold no record of has nothing to link
						// to, and is left as plain text rather than as a link that goes nowhere.
						row.contactId ? (
							<Tooltip title="Open the contact record — their call history is on it" arrow>
								<Box
									component="button"
									type="button"
									onClick={() => navigate(`/contacts/${row.contactId}/view`)}
									sx={{
										fontFamily: "monospace",
										fontSize: 13,
										p: 0,
										border: 0,
										background: "none",
										color: "primary.main",
										cursor: "pointer",
										textAlign: "left",
										"&:hover, &:focus-visible": { textDecoration: "underline" },
									}}
								>
									{row.phone}
								</Box>
							</Tooltip>
						) : (
							<Tooltip title="No contact on file for this number" arrow>
								<Box component="code" sx={{ fontFamily: "monospace", fontSize: 13, cursor: "help" }}>
									{row.phone}
								</Box>
							</Tooltip>
						)
					) : (
						<Tooltip title="Carried over from the old do-not-call tag. The number is held against the contact record rather than stored here, and suppression still applies.">
							<Chip
								label="On contact record"
								size="small"
								variant="outlined"
								color="default"
								{...(row.contactId
									? { onClick: () => navigate(`/contacts/${row.contactId}/view`), sx: { cursor: "pointer" } }
									: { sx: { cursor: "help" } })}
							/>
						</Tooltip>
					),
			},
			{
				header: { id: "reason", label: "Reason", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "reason",
				getCellValue: (row: Suppression) => REASON_LABELS[row.reason] ?? row.reason,
			},
			{
				header: { id: "source", label: "How it got here", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "source",
				getCellValue: (row: Suppression) => (
					<Chip label={SOURCE_LABELS[row.source] ?? row.source} size="small" variant="outlined" color="default" />
				),
			},
			{
				header: { id: "note", label: "Evidence", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "note",
				getCellValue: (row: Suppression) =>
					row.note ? (
						<Tooltip title={row.note}>
							<Typography
								variant="body2"
								sx={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
							>
								{row.note}
							</Typography>
						</Tooltip>
					) : (
						<Typography variant="body2" color="text.secondary">
							-
						</Typography>
					),
			},
			{
				header: { id: "requestedAt", label: "Requested", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "requestedAt",
				getCellValue: (row: Suppression) => formatDate(row.requestedAt),
			},
			{
				header: { id: "audit", label: "Recorded by", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "createdByName",
				getCellValue: (row: Suppression) => (
					<Typography variant="body2" color="text.secondary">
						{row.createdByName ?? (row.source === "agent_tool" || row.source === "disposition" ? "Agent" : "System")}
					</Typography>
				),
			},
			{
				header: { id: "status", label: "Status", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "active",
				getCellValue: (row: Suppression) =>
					row.active ? (
						<Chip label="Blocking calls" size="small" color="success" variant="filled" />
					) : (
						<Tooltip
							title={
								<>
									Lifted {formatDate(row.releasedAt)}
									{row.releasedByName ? ` by ${row.releasedByName}` : ""}
									{row.releaseNote ? ` \u2014 ${row.releaseNote}` : ""}
								</>
							}
						>
							<Chip label="Lifted" size="small" variant="outlined" sx={{ cursor: "help" }} />
						</Tooltip>
					),
			},
			{
				header: { id: "actions", label: "", align: "right" },
				accessorKey: "id",
				getCellValue: (row: Suppression) => (
					<TableActions
						actions={[
							{
								label: "Remove from list",
								icon: <ArrowCounterClockwiseIcon />,
								onClick: () => setToRelease(row),
								hidden: !row.active,
							},
						]}
					/>
				),
			},
		];
	}, []);

	return (
		<>
			<Box sx={{ p: 3 }}>
				<Stack spacing={3}>
					<Stack direction="row" spacing={3} sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
						<Box>
							<Typography variant="h4">Do not call</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Numbers no campaign in this account will dial
							</Typography>
						</Box>
						<Stack direction="row" spacing={1}>
							<ToggleButtonGroup
								size="small"
								exclusive
								value={showReleased ? "released" : "active"}
								onChange={(_, value: string | null) => {
									if (value) setShowReleased(value === "released");
								}}
								sx={{ height: 40, p: 0.25 }}
							>
								<ToggleButton value="active" sx={{ px: 1.5, py: 0.25, textTransform: "none", fontSize: 14 }}>
									Active
								</ToggleButton>
								<ToggleButton value="released" sx={{ px: 1.5, py: 0.25, textTransform: "none", fontSize: 14 }}>
									Lifted
								</ToggleButton>
							</ToggleButtonGroup>
							<Button startIcon={<PlusIcon />} variant="contained" onClick={() => setIsAddOpen(true)}>
								Add numbers
							</Button>
						</Stack>
					</Stack>

					<Alert severity="info" icon={<ProhibitIcon />}>
						<Typography variant="body2">
							This list is <strong>account-wide</strong>. A number here is blocked across every campaign, including
							ones created later and lists imported later. Records are never deleted &mdash; lifting one keeps the
							history, so you can always show what was honoured and when.
						</Typography>
					</Alert>

					<TableAnimation delay={0}>
						<Card>
							<CustomTable
								columns={columns}
								data={records}
								refetchData={refetchData}
								isLoading={isLoading}
								totalItems={totalItems}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<AddToDoNotCallDialog open={isAddOpen} onClose={() => setIsAddOpen(false)} />

			<ReleaseSuppressionDialog
				suppression={toRelease}
				onClose={() => setToRelease(null)}
				onReleased={refetchData}
			/>
		</>
	);
}
